import { MuteText } from '@elements/Common';
import { CommonUtility } from '@utility/common';
import { ErrorMessageConstant } from '@utility/constants/common';
import UserService from '@utility/services/user';
import { ToastMessage } from '@utility/toast';
import { useEffect, useMemo, useState } from 'react';
import type { IOrganizationUser } from 'types/common/user';
import type { IDropdownOption } from 'types/shipment/types';

interface IConfig {
  batch: {
    save_paper: string;
  };
  label: {
    output: string;
  };
  print: {
    computer: string;
    printer: string;
  };
  notifications: Record<string, string[]>;
}

export const userPreferencesHook = (
  isAuthenticated?: boolean,
  wmsPortal = false,
) => {
  const [data, setData] = useState<IConfig | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState('');

  const { notifications, batch, label, print } = useMemo(() => {
    delete data?.notifications?.CRM;
    const notifications = Object.keys(data?.notifications || {}).map(key => ({
      title: key,
      inApp: data?.notifications?.[key]?.includes?.('in-app'),
      email: data?.notifications?.[key]?.includes?.('email'),
    }));
    return {
      ...data,
      notifications,
    };
  }, [data]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const result = await UserService.preferences();
      setData(result.data);
    } catch (error) {
      ToastMessage.error(error.error || ErrorMessageConstant.defaultAPIError);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && !wmsPortal) {
      fetchData();
    }
  }, [isAuthenticated, wmsPortal]);

  const update = async reqData => {
    try {
      setProcessing('Saving');
      await UserService.updatePreferences({
        ...reqData,
      });

      setData({
        ...data,
        ...reqData,
      });
    } catch (error) {
      ToastMessage.error(error.error || ErrorMessageConstant.defaultAPIError);
    } finally {
      setProcessing('');
    }
  };

  return {
    notifications,
    batch,
    label,
    print,
    loading,
    update,
    processing,
  };
};

const USER_NAME =
  'sk:block sk:max-w-[250px] sk:overflow-hidden sk:text-ellipsis';

export const useUserHook = () => {
  const [data, setData] = useState<IOrganizationUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchData = async () => {
    try {
      setData([]);
      setLoading(true);
      const { data } = await UserService.getAllUsers();
      setData(data);
    } catch (error) {
      setError(error.error || ErrorMessageConstant.defaultAPIError);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const searchFunction = ({ item }, query) =>
    CommonUtility.searchQueryCompare(item?.email, query) ||
    CommonUtility.searchQueryCompare(item?.first_name, query);

  const allUsersOptions = useMemo(
    () =>
      data?.map(user => ({
        text: user?.email,
        value: user?._id,
        item: user,
        content: (
          <div className="sk:flex! sk:flex-col!">
            <span className={USER_NAME}>{user?.email}</span>
            <MuteText className={USER_NAME}>{user?.first_name}</MuteText>
          </div>
        ),
      })) as IDropdownOption[],
    [data],
  );

  return {
    data,
    loading,
    error,
    allUsersOptions,
    searchFunction,
  };
};
